import axios, {AxiosRequestConfig, AxiosResponse} from "axios";
import {ref} from "vue";
import JwtService from "@/core/services/JwtService";

// The cabinet keeps working without a connection to the server: the pages open (service worker, public/service-worker.js)
// and show the data that was saved when it was loaded last time. Here is the second half: the answers of the server.
//  - every answer to a reading request is saved (IndexedDB) for the user who is logged in;
//  - when the server can not be reached, the saved answer is given instead, and the cabinet is "offline" (a banner tells it);
//  - requests that change something (payments, tariffs, requests...) are never answered from the saved data: they fail
//    with a message, so nobody thinks that a payment was made while the connection was down;
//  - the saved data is deleted when the user logs out.

// has the server been unreachable? (the page shows a banner and takes the saved data)
export const isOffline = ref(typeof navigator !== "undefined" ? navigator.onLine === false : false);
// when the data on the screen was saved (ms); 0 = not known
export const savedAt = ref(0);
// the connection is back after a time offline (the banner offers to refresh the page)
export const justRestored = ref(false);

const DB_NAME = "n4-offline";
const STORE = "responses";
const MARK = "n4:offline-data";
const PROBE_MS = 15000;

// the reading requests of the old API (they are POST): their answers can be shown again. Everything else that is POST, PUT or
// DELETE changes something.
const READ_POST = /\/usertvs\/(verify-user|get-users-payments-data|user-requests-data)$/;
// answers about the state of the equipment *now*: an old one would be a lie
const LIVE_GET = /(support-phones)/;

const apiHosts = (): string[] => {
    const hosts: string[] = [];
    [process.env.VUE_APP_API_URL, process.env.VUE_APP_API_BASE_URL, process.env.VUE_APP_SWEET_TV_API_URL].forEach((url) => {
        try {
            if (url) hosts.push(new URL(url).host);
        } catch (e) {
            // not an address
        }
    });
    return hosts;
};

const fullUrl = (config: AxiosRequestConfig): string => {
    const url = config.url || "";
    if (/^https?:\/\//i.test(url)) return url;
    const base = (config.baseURL || "").replace(/\/+$/, "");
    return base + "/" + url.replace(/^\/+/, "");
};

const hostOf = (config: AxiosRequestConfig): string => {
    try {
        return new URL(fullUrl(config), window.location.href).host;
    } catch (e) {
        return "";
    }
};

const isApi = (config: AxiosRequestConfig) => apiHosts().includes(hostOf(config));

const methodOf = (config: AxiosRequestConfig) => String(config.method || "get").toLowerCase();

// can an old answer replace the live one for this request?
const isReadable = (config: AxiosRequestConfig): boolean => {
    if (!isApi(config)) return false;
    const url = fullUrl(config).split("?")[0];
    if (methodOf(config) === "get") return !LIVE_GET.test(url);
    return methodOf(config) === "post" && READ_POST.test(url);
};

const stable = (value: any): string => {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") {
        try {
            return stable(JSON.parse(value));
        } catch (e) {
            return value;
        }
    }
    if (Array.isArray(value)) return "[" + value.map(stable).join(",") + "]";
    if (typeof value === "object") {
        return (
            "{" +
            Object.keys(value)
                .filter((k) => !["api_token", "token", "fn"].includes(k) && typeof value[k] !== "function")
                .sort()
                .map((k) => k + ":" + stable(value[k]))
                .join(",") +
            "}"
        );
    }
    return String(value);
};

// a short fingerprint: the saved data belongs to the session of one user
const hash = (text: string): string => {
    let h = 5381;
    for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
};

const keyOf = (config: AxiosRequestConfig): string =>
    [hash(JwtService.getToken() || ""), methodOf(config), fullUrl(config), stable(config.params), stable(config.data)].join("|");

// ---- IndexedDB (every call may fail: a private window, a full disk... then the cabinet just works without the saved data)

let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            if (typeof indexedDB === "undefined") return reject(new Error("no indexedDB"));
            const request = indexedDB.open(DB_NAME, 1);
            request.onupgradeneeded = () => request.result.createObjectStore(STORE);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
        dbPromise.catch(() => (dbPromise = null));
    }
    return dbPromise;
};

type Saved = {data: any; at: number};

const readSaved = async (key: string): Promise<Saved | null> => {
    try {
        const db = await openDb();
        return await new Promise<Saved | null>((resolve) => {
            const request = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => resolve(null);
        });
    } catch (e) {
        return null;
    }
};

const writeSaved = async (key: string, data: any) => {
    try {
        const db = await openDb();
        db.transaction(STORE, "readwrite").objectStore(STORE).put({data, at: Date.now()}, key);
        window.localStorage.setItem(MARK, "1");
    } catch (e) {
        // not saved: the next load asks the server again
    }
};

// the data of the user is deleted from this browser (a log out, a session that is over)
export const clearOfflineCache = () => {
    try {
        if (window.localStorage.getItem(MARK) === null) return;
        window.localStorage.removeItem(MARK);
    } catch (e) {
        return;
    }
    dbPromise = null;
    try {
        indexedDB.deleteDatabase(DB_NAME);
    } catch (e) {
        // nothing to delete
    }
};

// ---- the state "offline"

let probeTimer: number | null = null;
let wentOfflineAt = 0;

const probe = async () => {
    if (navigator.onLine === false) return;
    try {
        // any answer of the server (even an error page) says that it can be reached
        await fetch(process.env.VUE_APP_API_URL + "ping", {method: "GET", cache: "no-store", mode: "no-cors"});
        goOnline();
    } catch (e) {
        // still no connection
    }
};

const goOffline = (when?: number) => {
    if (when && (!savedAt.value || when < savedAt.value)) savedAt.value = when;
    if (isOffline.value) return;
    isOffline.value = true;
    justRestored.value = false;
    wentOfflineAt = Date.now();
    document.body.classList.add("is-offline");
    if (probeTimer === null) probeTimer = window.setInterval(probe, PROBE_MS);
};

const goOnline = () => {
    if (!isOffline.value) return;
    isOffline.value = false;
    document.body.classList.remove("is-offline");
    if (probeTimer !== null) {
        window.clearInterval(probeTimer);
        probeTimer = null;
    }
    // a short failure of one request is not worth a banner
    justRestored.value = Date.now() - wentOfflineAt > 5000;
    savedAt.value = 0;
};

export const dismissRestored = () => (justRestored.value = false);

// "this needs the server": the banner shows it
export const OFFLINE_BLOCKED_EVENT = "n4:offline-blocked";

const blocked = (config: AxiosRequestConfig) => {
    window.dispatchEvent(new CustomEvent(OFFLINE_BLOCKED_EVENT));
    const error: any = new Error("Offline: the request needs a connection");
    error.config = config;
    error.isAxiosError = true;
    error.offline = true;
    return error;
};

const savedResponse = (config: AxiosRequestConfig, saved: Saved): AxiosResponse => {
    (config as any).__offline = true;
    return {data: saved.data, status: 200, statusText: "OK (saved)", headers: {}, config, request: {}};
};

let installed = false;

// Installs the interceptors on the axios that the whole cabinet uses. Called once, at the start of the application.
export const initOffline = () => {
    if (installed) return;
    installed = true;

    window.addEventListener("offline", () => goOffline());
    window.addEventListener("online", () => probe());
    if (navigator.onLine === false) document.body.classList.add("is-offline");

    axios.interceptors.request.use(async (config) => {
        if (!isApi(config)) return config;

        // the browser says there is no network: the server is not even asked
        if (navigator.onLine === false) {
            goOffline();
            if (isReadable(config)) {
                const saved = await readSaved(keyOf(config));
                if (saved) {
                    goOffline(saved.at);
                    config.adapter = () => Promise.resolve(savedResponse(config, saved));
                    return config;
                }
            }
            config.adapter = () => Promise.reject(blocked(config));
        }
        return config;
    });

    axios.interceptors.response.use(
        (response) => {
            const config = response.config;
            if (!(config as any).__offline && isApi(config)) {
                if (isOffline.value) goOnline();

                const errors = response.data && response.data.errors;
                const failed = errors && typeof errors === "object" && Object.keys(errors).length > 0;
                if (isReadable(config) && response.status === 200 && !failed) {
                    writeSaved(keyOf(config), response.data);
                }
            }
            return response;
        },
        async (error) => {
            const config: AxiosRequestConfig | undefined = error && error.config;
            // the server answered (an error of its own) or the request was not ours: nothing to do with the connection
            if (!config || error.response || error.offline || !isApi(config) || axios.isCancel(error)) {
                return Promise.reject(error);
            }

            goOffline();
            if (isReadable(config)) {
                const saved = await readSaved(keyOf(config));
                if (saved) {
                    goOffline(saved.at);
                    return savedResponse(config, saved);
                }
            }
            if (methodOf(config) !== "get") {
                window.dispatchEvent(new CustomEvent(OFFLINE_BLOCKED_EVENT));
            }
            return Promise.reject(error);
        }
    );
};
